import { Fragment } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { TopicModuleRow } from '../components/inventory/TopicModuleRow';
import { TopicModuleGroup } from '../components/inventory/TopicModuleGroup';
import { StudyChapterLink } from '../components/inventory/StudyChapterLink';
import { ThirdBidChooser } from '../components/inventory/ThirdBidChooser';
import { ComingSoonChip } from '../components/inventory/ComingSoonChip';
import { BidText } from '../components/common/BidToken';
import { usePageTitle } from '../hooks/usePageTitle';
import { findTopic, isFamily, rowGroupsFor } from '../config/moduleCatalog';
import type { ModuleFamily, TopicDefinition, TopicRow } from '../config/moduleCatalog';

/**
 * A topic page (feature 023, US4) — `/inventory/:family/:topicKey`.
 *
 * Per `topic-1c.html`: a "← All bids" back link, the topic title with
 * "Study Chapter NN" right-aligned on the same line, then one row per module
 * with Practice · Master (the per-row Study button was removed 2026-07-22).
 *
 * The title-line link was "Study all chapters" until the chapter set was completed;
 * it now points at the one chapter that covers this topic. See `StudyChapterLink`.
 */
export function TopicPage() {
  const { family, topicKey } = useParams<{ family: string; topicKey: string }>();

  if (!isFamily(family)) {
    return <Navigate to="/inventory/precision" replace />;
  }

  const topic = findTopic(topicKey);
  if (!topic) {
    return <Navigate to={`/inventory/${family}`} replace />;
  }

  return <TopicContent family={family} topic={topic} />;
}

function TopicContent({ family, topic }: { family: ModuleFamily; topic: TopicDefinition }) {
  usePageTitle(`${topic.title.replace(/\s+—.*$/, '')} — Hawkes Bridge`);

  // A hidden topic (feature 033's Overcalls inventory; Preempts since 2026-10-16) is reached
  // from the dashboard, not from an inventory list, so its back link returns there and says
  // so — and its rows may span both families (Preempts), each Practice link under its own,
  // each family's rows boxed under the heading the catalog gives it (2026-10-19). A topic
  // without headings gets one plain group and renders exactly as it always has.
  const groups = rowGroupsFor(topic, family);
  const backTo = topic.backTo ?? `/inventory/${family}`;

  return (
    <div className="mx-auto w-full max-w-[1080px] px-4 py-8">
      <Link
        to={backTo}
        className="text-[13px] font-bold text-primary-600 no-underline hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2"
        data-testid="topic-back-link"
      >
        {backTo === '/dashboard' ? '← Dashboard' : '← All bids'}
      </Link>

      {/* Title, brass rule and the Study link share ONE flex container so that on a
          phone the rule can sit between the title and the link. The link used to wrap
          onto its own line while the rule stayed a later sibling, which left the link
          stranded between the title and the rule the rule is meant to underline.
          `order` re-sequences the three on `narrow:` instead of rendering the link
          twice — a second copy would be a duplicate link in the DOM.

          `narrow:` (not `phone-pt:`) because the collision is width-driven and hits
          landscape phones too: the reported floor is a 568px-wide landscape phone, and
          `narrow:` covers every viewport below 768 in either orientation plus the
          700–950 landscape-phone band and, since feature 031, the 951–1023 landscape
          band at 300–500 tall (a large phone sideways). Tablet portrait is excluded —
          768px has the room to keep the title and link on one line. */}
      <div className="mb-6 mt-3 flex flex-wrap items-center gap-x-3">
        <h1 className="order-1 min-w-0 flex-1 font-serif text-[26px] text-ink">
          <BidText text={topic.title} />
        </h1>
        {/* Both families. Competitive has no chapter set of its own, but it lists
            the same topics as precision, so a competitive topic studies the same
            Precision chapter its bid belongs to. Renders nothing for a topic with
            no chapter. */}
        <StudyChapterLink
          topic={topic}
          className="order-2 narrow:order-3 narrow:mt-2 narrow:block narrow:w-full narrow:text-right"
        />
        {/* `w-full` is what forces the rule onto its own line in both arrangements;
            the bar itself keeps its 16-unit width inside. */}
        <div className="order-3 mt-2.5 w-full narrow:order-2" aria-hidden="true">
          <div className="h-0.5 w-16 bg-brass" />
        </div>
      </div>

      {topic.enabled ? (
        <div data-testid="module-list">
          {groups.map(({ family: groupFamily, heading, rows }) =>
            heading ? (
              <TopicModuleGroup key={groupFamily} family={groupFamily} heading={heading}>
                {rows.map((row) => renderRow(row, topic))}
              </TopicModuleGroup>
            ) : (
              <Fragment key={groupFamily}>{rows.map((row) => renderRow(row, topic))}</Fragment>
            )
          )}
        </div>
      ) : (
        <div
          className="rounded-xl border border-line bg-card p-6"
          data-testid="topic-coming-soon"
        >
          <ComingSoonChip />
          <p className="mt-3 text-sm text-ink-soft">
            This topic is still in development with Jeff. It will appear here when the modules are
            ready.
          </p>
        </div>
      )}
    </div>
  );
}

function renderRow({ family: rowFamily, module }: TopicRow, topic: TopicDefinition) {
  return (
    <TopicModuleRow
      key={`${rowFamily}.${module.slug}`}
      module={module}
      topic={topic}
      // A redirect WITHOUT variants ("Jeff's problems") is left to the row, which renders a
      // plain Practice link to the route; one WITH variants gets the chooser.
      practiceTo={
        module.redirect ? undefined : `/module-practice/${rowFamily}/${topic.key}/${module.slug}`
      }
      practiceSlot={module.redirect?.variants?.length ? <ThirdBidChooser module={module} /> : undefined}
    />
  );
}
